/**
 * Adaptive Engine — Orchestrator
 *
 * Session generation and debug reporting for the adaptive question
 * selection system. This file coordinates the sub-modules:
 *
 * - selection/selectionConfig.ts — V11_CONFIG, debugLog
 * - selection/familyScoring.ts — priority scoring, target tier
 * - selection/tierWindow.ts — tier window construction
 * - selection/prerequisiteCheck.ts — LDG prerequisite gating
 * - selection/questionSelector.ts — single-question selection algorithm
 * - tierProgress.ts — tier progress for UI
 * - subSkillMastery.ts — subSkill mastery for UI
 *
 * O-2: Split from 2082-line god-file into focused modules.
 * All external consumers import via the barrel (src/mastery/index.ts).
 */

import {
  type ModuleId,
  type Question,
  QUESTIONS_BY_FAMILY,
  getTierConfig,
  getAllowedTiersForGroup,
  getTierRangeForGroup,
  getFamiliesForModule,
} from '../content'
import { QuestionState } from './types'
import { GroupId } from '../config/systemConfig'
import { createSessionRng } from '../utils/deterministicRng'
import { FamilyMasteryState, getFamilyMasteryKey } from './familyMasteryEngine'
import {
  debug as logDebug,
  warn as logWarn,
  error as logError,
  group,
  groupEnd,
} from '../utils/logger'
import { V11_CONFIG, ADAPTIVE_DEBUG } from './selection/selectionConfig'
import { isMasteryStable } from './selection/familyScoring'
import { selectAdaptiveQuestionV11 } from './selection/questionSelector'
import { getQuestionStateKey } from './questionAttemptHistory'
import { detectCognitiveLevel } from '../governance/cognitiveLevel'

// ============================================================
// SESSION GENERATION
// ============================================================

/**
 * Generate a practice session using the v11.2 familyId-first algorithm
 * with strict pedagogical rules and group/tier gating.
 *
 * v11.2 Enhancements:
 * - Per-family question cap (max 2 per family)
 * - Mastery confidence checks (min 5 attempts for stable mastery)
 * - Recency-based question exclusion
 * - Controlled reinforcement (70% max mastered content)
 * - Minimum family diversity (2+ families when possible)
 * - Enhanced debug summary with pedagogical metrics
 * - Soft preference for interactive types
 * v11.8: B/M/E period tier for priority boost (QF-26 Optie B)
 *
 * @param moduleId - Module to practice
 * @param familyMastery - FamilyId mastery states from AppState
 * @param selectedGroup - Current school group (ENFORCED for tier gating)
 * @param questionStates - Per-question states (for mastered-count tracking)
 * @param count - Number of questions to generate
 * @param recentFamilyIds - Recently-seen familyIds (cross-session cooldown)
 * @param recentQuestionIds - Recently-seen questionIds (exclude from selection)
 * @param sessionSeed - Optional seed for deterministic generation
 * @param startingTier - Optional starting tier for cold starts (B/M/E)
 * @returns Array of questions for the session
 */
export function getAdaptiveSessionV11(
  moduleId: ModuleId,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  questionStates: Record<string, QuestionState>,
  count: number = 10,
  recentFamilyIds: string[] = [],
  recentQuestionIds: string[] = [],
  sessionSeed?: number,
  startingTier?: number,
  /** Optional timestamp for deterministic testing (H-5 audit). Defaults to Date.now(). */
  now?: number,
): Question[] {
  // Create seeded RNG
  const seed = sessionSeed ?? Date.now()
  const rng = createSessionRng(moduleId, selectedGroup, seed)

  // CRITICAL: Compute allowed tiers for this group ONCE
  let allowedTiers = getAllowedTiersForGroup(selectedGroup)
  const tierRange = getTierRangeForGroup(selectedGroup)
  logDebug(
    `\n🎲 v11.2 Session Generation START: module=${moduleId}, group=${selectedGroup}, seed=${rng.seed}`,
  )
  logDebug(
    `   📊 Allowed tiers: [${tierRange.min}-${tierRange.max}] (${allowedTiers.size} tiers)`,
  )

  // Pre-flight validation
  const allModuleFamilies = getFamiliesForModule(moduleId)
  logDebug(`   📋 Total families in module: ${allModuleFamilies.length}`)
  if (allModuleFamilies.length === 0) {
    logError(`v11.2: No families found in taxonomy for module ${moduleId}`)
    return []
  }
  let eligibleFamilies = allModuleFamilies.filter((f) =>
    allowedTiers.has(f.tier),
  )

  // v11.7: B/M/E Cold Start Soft Boost (Optie A)
  // If the student has NO mastery data for any eligible family in this module,
  // we restrict the allowed tiers to start at their B/M/E level.
  if (startingTier && eligibleFamilies.length > 0) {
    const hasAnyMasteryData = eligibleFamilies.some((f) => {
      const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup)
      const mastery = familyMastery[masteryKey]
      return mastery && mastery.attempts > 0
    })

    if (!hasAnyMasteryData) {
      logDebug(
        `   🌱 COLD START DETECTED: No mastery data found. Applying B/M/E soft boost to tier ${startingTier}.`,
      )
      // Restrict to startingTier and startingTier + 1 (to give a small window)
      // but ensure we don't go outside the group's allowed tiers
      const boostedTiers = new Set<number>()
      if (allowedTiers.has(startingTier)) boostedTiers.add(startingTier)
      if (allowedTiers.has(startingTier + 1)) boostedTiers.add(startingTier + 1)

      if (boostedTiers.size > 0) {
        allowedTiers = boostedTiers
        eligibleFamilies = allModuleFamilies.filter((f) =>
          allowedTiers.has(f.tier),
        )
        logDebug(
          `   🚀 Boosted allowed tiers: [${Array.from(allowedTiers).join(',')}] (${eligibleFamilies.length} families)`,
        )
      }
    }
  }

  logDebug(
    `   ✅ Eligible families (within tier range): ${eligibleFamilies.length}/${allModuleFamilies.length}`,
  )
  if (eligibleFamilies.length === 0) {
    logWarn(
      `v11.2: No families for module ${moduleId} within allowed tiers [${tierRange.min}-${tierRange.max}] for group ${selectedGroup}`,
    )
    logWarn(
      `   Module has ${allModuleFamilies.length} families, but none match the group's tier range.`,
    )
    logWarn(
      `   This usually means the module content is for a different grade level.`,
    )
    return []
  }

  // Check if any eligible families have questions
  let familiesWithQuestions = 0
  let totalQuestionsAvailable = 0
  const familyQuestionCounts: Record<string, number> = {}
  eligibleFamilies.forEach((family) => {
    const questions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
    const inTierQuestions = questions.filter((q) => allowedTiers.has(q.tier))
    familyQuestionCounts[family.familyId] = inTierQuestions.length
    if (inTierQuestions.length > 0) {
      familiesWithQuestions++
      totalQuestionsAvailable += inTierQuestions.length
    }
  })
  logDebug(
    `   📚 Families with questions: ${familiesWithQuestions}/${eligibleFamilies.length}`,
  )
  logDebug(`   📝 Total questions available: ${totalQuestionsAvailable}`)

  // v11.2: Enhanced mastery distribution with confidence metrics
  const masteryDistribution = {
    unstarted: 0,
    struggling: 0,
    learning: 0,
    masteredUnstable: 0,
    masteredStable: 0,
  }
  eligibleFamilies.forEach((family) => {
    // CRITICAL: Use group-scoped mastery key
    const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
    const mastery = familyMastery[masteryKey]
    if (!mastery || mastery.attempts === 0) {
      masteryDistribution.unstarted++
    } else if (mastery.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
      masteryDistribution.struggling++
    } else if (mastery.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) {
      masteryDistribution.learning++
    } else {
      const isStable = isMasteryStable(mastery)
      if (isStable) {
        masteryDistribution.masteredStable++
      } else {
        masteryDistribution.masteredUnstable++
      }
    }
  })
  logDebug(`   📊 Mastery distribution:`)
  logDebug(`      - Unstarted: ${masteryDistribution.unstarted}`)
  logDebug(`      - Struggling (<40): ${masteryDistribution.struggling}`)
  logDebug(`      - Learning (40-85): ${masteryDistribution.learning}`)
  logDebug(
    `      - Mastered (unstable, <5 attempts): ${masteryDistribution.masteredUnstable}`,
  )
  logDebug(
    `      - Mastered (stable, ≥5 attempts): ${masteryDistribution.masteredStable}`,
  )
  if (familiesWithQuestions === 0) {
    logWarn(
      `⚠️ v11.2: Module ${moduleId} has ${eligibleFamilies.length} eligible families for group ${selectedGroup}, but NONE have questions within allowed tiers [${tierRange.min}-${tierRange.max}]`,
    )
    logWarn(
      `   This indicates missing question content. Check seed data for this module.`,
    )
    return []
  }

  // Check if all families with questions are mastered (stable)
  const familiesWithQuestionsAndStableMastery = eligibleFamilies.filter((f) => {
    const hasQuestions = familyQuestionCounts[f.familyId] > 0
    // CRITICAL: Use group-scoped mastery key
    const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup)
    const mastery = familyMastery[masteryKey]
    return hasQuestions && isMasteryStable(mastery)
  }).length
  const reinforcementMode =
    familiesWithQuestionsAndStableMastery === familiesWithQuestions &&
    familiesWithQuestions > 0
  if (reinforcementMode) {
    logDebug(
      `   ⭐ REINFORCEMENT MODE: All families with questions are stably mastered!`,
    )
  }
  logDebug(
    `   ✅ Pre-flight check passed: ${familiesWithQuestions}/${eligibleFamilies.length} families have questions (${totalQuestionsAvailable} total available)`,
  )

  // v11.6: Dynamic per-family cap
  // When there are fewer eligible families than needed to fill the session
  // at the default cap (2), increase the cap so the session can be filled.
  // Example: TOPO group 6 has 4 families × 2 = 8 < 10 target → raise cap to 3
  const effectiveMaxPerFamily = Math.max(
    V11_CONFIG.MAX_QUESTIONS_PER_FAMILY,
    Math.ceil(count / Math.max(1, eligibleFamilies.length)),
  )
  if (effectiveMaxPerFamily > V11_CONFIG.MAX_QUESTIONS_PER_FAMILY) {
    logDebug(
      `   📊 v11.6: Dynamic per-family cap raised: ${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY} → ${effectiveMaxPerFamily} (${eligibleFamilies.length} families for ${count} questions)`,
    )
  }

  // v11.2: Session tracking with enhanced metrics
  const session: Question[] = []
  const sessionQuestionIds = new Set<string>()
  const sessionFamilyIds = new Set<string>()
  const sessionFamilyQuestionCounts = new Map<string, number>()
  const sessionTypeHistory: string[] = []
  // v11.3: Track skills and cognitive levels for interleaving/diversity (P-10/P-11)
  const sessionSkills: string[] = []
  const sessionCognitiveLevels: string[] = []
  const recentFamilySet = new Set(
    recentFamilyIds.slice(0, V11_CONFIG.RECENT_FAMILY_COOLDOWN),
  )
  const recentQuestionSet = new Set(recentQuestionIds)
  let masteredCount = 0
  let totalRetries = 0
  let maxFallbackLevel = 0
  const selectionLog: {
    slot: number
    questionId: string | null
    familyId: string | null
    tier: number | null
    reason: string
    fallbackLevel: number
  }[] = []
  logDebug(`\n🔄 Starting question selection (target: ${count} questions)...`)
  logDebug(
    `   🎯 Pedagogical constraints: max ${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY} per family, ${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)} max mastered (${Math.round(V11_CONFIG.MAX_MASTERED_PERCENTAGE * 100)}%)`,
  )
  for (let i = 0; i < count; i++) {
    let slotRetries = 0
    let slotFilled = false
    logDebug(`\n   🎯 Slot ${i + 1}/${count}:`)
    logDebug(
      `      - Unique families used: ${sessionFamilyIds.size}/${eligibleFamilies.length}`,
    )
    logDebug(
      `      - Mastered count: ${masteredCount}/${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)}`,
    )
    logDebug(`      - Questions selected: ${session.length}`)
    while (
      slotRetries < V11_CONFIG.MAX_RETRIES_PER_SLOT &&
      totalRetries < V11_CONFIG.MAX_TOTAL_RETRIES
    ) {
      const { question, reason, fallbackLevel } = selectAdaptiveQuestionV11(
        moduleId,
        familyMastery,
        selectedGroup,
        allowedTiers,
        sessionFamilyIds,
        sessionQuestionIds,
        recentFamilySet,
        recentQuestionSet,
        sessionTypeHistory,
        masteredCount,
        sessionFamilyQuestionCounts,
        count,
        rng,
        effectiveMaxPerFamily,
        startingTier,
        now,
        sessionSkills,
        sessionCognitiveLevels,
      )
      if (!question) {
        logDebug(`      ❌ No question found: ${reason}`)
        selectionLog.push({
          slot: i,
          questionId: null,
          familyId: null,
          tier: null,
          reason,
          fallbackLevel,
        })
        maxFallbackLevel = Math.max(maxFallbackLevel, fallbackLevel)
        break
      }

      // SAFETY CHECK: Verify the selected question is within allowed tiers
      if (!allowedTiers.has(question.tier)) {
        logError(
          `      🚨 TIER VIOLATION: Question ${question.id} has tier ${question.tier} but group ${selectedGroup} allows [${tierRange.min}-${tierRange.max}]. REJECTED.`,
        )
        sessionQuestionIds.add(question.id)
        slotRetries++
        totalRetries++
        continue
      }

      // v11.2: Check per-family cap (use effective cap)
      const familyUsage =
        sessionFamilyQuestionCounts.get(question.familyId!) || 0
      if (
        familyUsage >= effectiveMaxPerFamily &&
        eligibleFamilies.length >= V11_CONFIG.MIN_FAMILIES_PER_SESSION
      ) {
        logDebug(
          `      ⚠️ Family ${question.familyId} would exceed per-family cap (${familyUsage}/${effectiveMaxPerFamily}), retrying...`,
        )
        sessionQuestionIds.add(question.id)
        slotRetries++
        totalRetries++
        continue
      }

      // Valid question — add to session
      session.push(question)
      sessionQuestionIds.add(question.id)
      sessionTypeHistory.push(question.questionType || 'multiple_choice')
      // v11.3: Track skill and cognitive level for interleaving/diversity
      if (question.skill) {
        sessionSkills.push(question.skill)
      }
      sessionCognitiveLevels.push(detectCognitiveLevel(question))
      if (question.familyId) {
        sessionFamilyIds.add(question.familyId)
        sessionFamilyQuestionCounts.set(
          question.familyId,
          (sessionFamilyQuestionCounts.get(question.familyId) || 0) + 1,
        )
      }

      // Track mastered count
      // FIX F-MAS-R1-MASTERED-CAP-INTERLOCK:
      // Originally counted only when questionStates[stateKey].status === 'mastered'
      // (per-question UI signal). This missed cases where the *family* has stable
      // ELO mastery (score >= MASTERED_THRESHOLD, attempts >= MASTERY_CONFIDENCE_MIN_ATTEMPTS)
      // but the specific question has not yet been individually flagged 'mastered'.
      // The mastered cap (MAX_MASTERED_PERCENTAGE) is a *pedagogical* constraint —
      // "≤ 70% of a session may come from content the learner already controls" —
      // so we count via EITHER signal.
      const stateKey = getQuestionStateKey(question.id, selectedGroup)
      const state = questionStates[stateKey]
      const isQuestionMastered = state?.status === 'mastered'
      let isFamilyMastered = false
      if (question.familyId) {
        const familyKey = getFamilyMasteryKey(question.familyId, selectedGroup)
        isFamilyMastered = isMasteryStable(familyMastery[familyKey])
      }
      if (isQuestionMastered || isFamilyMastered) {
        masteredCount++
      }
      maxFallbackLevel = Math.max(maxFallbackLevel, fallbackLevel)
      logDebug(
        `      ✅ Selected: ${question.id} (family: ${question.familyId}, tier: ${question.tier}, fallback: ${fallbackLevel}) - ${reason}`,
      )
      selectionLog.push({
        slot: i,
        questionId: question.id,
        familyId: question.familyId || null,
        tier: question.tier,
        reason,
        fallbackLevel,
      })
      slotFilled = true
      break
    }
    if (!slotFilled) {
      if (totalRetries >= V11_CONFIG.MAX_TOTAL_RETRIES) {
        logWarn(
          `   ⚠️ Hit total retry limit. Generated ${session.length}/${count} questions.`,
        )
      }
      break
    }
  }

  // v11.2: Enhanced session summary with pedagogical metrics
  logDebug(`\n📊 v11.2 Session Generation COMPLETE:`)
  logDebug(`   ✅ Questions generated: ${session.length}/${count}`)
  if (session.length > 0) {
    const tierCounts = new Map<string, number>()
    const familyIds: string[] = []
    const familyUsageCounts = new Map<string, number>()
    let anyOutOfScope = false
    let masteredInSession = 0
    session.forEach((q) => {
      const label = getTierConfig(q.tier)?.levelCode || `T${q.tier}`
      tierCounts.set(label, (tierCounts.get(label) || 0) + 1)
      if (q.familyId) {
        if (!familyIds.includes(q.familyId)) {
          familyIds.push(q.familyId)
        }
        familyUsageCounts.set(
          q.familyId,
          (familyUsageCounts.get(q.familyId) || 0) + 1,
        )
      }
      if (!allowedTiers.has(q.tier)) {
        anyOutOfScope = true
      }
      const qStateKey = getQuestionStateKey(q.id, selectedGroup)
      const qState = questionStates[qStateKey]
      if (qState?.status === 'mastered') {
        masteredInSession++
      }
    })
    const summary = Array.from(tierCounts.entries())
      .map(([label, n]) => `${label}:${n}`)
      .join(', ')
    const maxUsage = Math.max(...Array.from(familyUsageCounts.values()))
    const familyUsageSummary = Array.from(familyUsageCounts.entries())
      .map(([fId, n]) => `${fId}:${n}`)
      .join(', ')
    logDebug(`\n   📈 PEDAGOGICAL METRICS:`)
    logDebug(`      - Tier distribution: [${summary}]`)
    logDebug(`      - Unique families used: ${familyIds.length}`)
    logDebug(`      - Family usage: ${familyUsageSummary}`)
    logDebug(
      `      - Max questions per family: ${maxUsage}/${effectiveMaxPerFamily}`,
    )
    logDebug(
      `      - Mastered questions: ${masteredInSession}/${session.length} (${Math.round((masteredInSession / session.length) * 100)}%)`,
    )
    logDebug(
      `      - Mastered cap: ${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)} (${Math.round(V11_CONFIG.MAX_MASTERED_PERCENTAGE * 100)}%)`,
    )
    logDebug(`      - Reinforcement mode: ${reinforcementMode}`)
    logDebug(`      - Max fallback level reached: ${maxFallbackLevel}`)
    logDebug(
      `      - Tier gating: ${anyOutOfScope ? '❌ VIOLATED' : '✅ STRICT'}`,
    )

    // v11.2: Pedagogical warnings
    if (
      familyIds.length < V11_CONFIG.MIN_FAMILIES_PER_SESSION &&
      eligibleFamilies.length >= V11_CONFIG.MIN_FAMILIES_PER_SESSION
    ) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Only ${familyIds.length} families used (recommended: ${V11_CONFIG.MIN_FAMILIES_PER_SESSION}+)`,
      )
    }
    if (maxUsage > effectiveMaxPerFamily) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Family cap exceeded (${maxUsage}/${effectiveMaxPerFamily})`,
      )
    }
    if (
      masteredInSession > Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)
    ) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Mastered cap exceeded (${masteredInSession}/${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)})`,
      )
    }
    if (anyOutOfScope) {
      logError(
        `   🚨 CRITICAL: Session contains questions OUTSIDE allowed tiers for group ${selectedGroup}!`,
      )
    }
    logDebug(
      `\n   🎯 Group: ${selectedGroup} | Tiers: [${tierRange.min}-${tierRange.max}]`,
    )
  } else {
    logWarn(
      `   ⚠️ Empty session generated for ${moduleId} in group ${selectedGroup}.`,
    )
    logWarn(
      `   📊 Diagnosis: ${familiesWithQuestions} families have ${totalQuestionsAvailable} questions available, but selection failed.`,
    )
    logWarn(`   🔍 Possible causes:`)
    logWarn(
      `      - All available questions already used in session (need more question variants)`,
    )
    logWarn(`      - Pedagogical constraints too strict for available content`)
    logWarn(`      - All families already selected (need more diverse content)`)
    logWarn(
      `   💡 TIP: Check the selection log below for detailed per-slot diagnostics`,
    )
  }

  // Debug: log selection details
  if (ADAPTIVE_DEBUG && selectionLog.length > 0) {
    group(`🔍 v11.2 Selection Log (${moduleId}, group ${selectedGroup})`)
    selectionLog.forEach((entry) => {
      if (entry.questionId) {
        logDebug(
          `  Slot ${entry.slot + 1}: ${entry.questionId} (family: ${entry.familyId}, tier: ${entry.tier}, fallback: ${entry.fallbackLevel}) — ${entry.reason}`,
        )
      } else {
        logDebug(
          `  Slot ${entry.slot + 1}: NO QUESTION (fallback: ${entry.fallbackLevel}) — ${entry.reason}`,
        )
      }
    })
    groupEnd()
  }
  return session
}

// ============================================================
// DEBUG / QA REPORT
// ============================================================

export interface SelectionDebugReport {
  group: GroupId
  allowedTiers: number[]
  modules: {
    moduleId: ModuleId
    totalFamilies: number
    eligibleFamilies: number
    familiesWithQuestions: number
    missingFamilies: string[]
    tierCoverage: Record<number, number>
  }[]
}

/**
 * Generate a debug report showing content availability per module/group.
 * Use this to identify missing content that would force out-of-scope fallback.
 */
export function generateSelectionDebugReport(
  group: GroupId,
  moduleIds: ModuleId[],
): SelectionDebugReport {
  const allowedTiers = getAllowedTiersForGroup(group)
  const allowedArray = Array.from(allowedTiers).sort((a, b) => a - b)
  const modules = moduleIds.map((moduleId) => {
    const allFamilies = getFamiliesForModule(moduleId)
    const eligibleFamilies = allFamilies.filter((f) => allowedTiers.has(f.tier))
    const tierCoverage: Record<number, number> = {}
    const missingFamilies: string[] = []
    let familiesWithQuestions = 0
    for (const family of eligibleFamilies) {
      const questions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
      const inTierQuestions = questions.filter((q) => allowedTiers.has(q.tier))
      if (inTierQuestions.length > 0) {
        familiesWithQuestions++
        inTierQuestions.forEach((q) => {
          tierCoverage[q.tier] = (tierCoverage[q.tier] || 0) + 1
        })
      } else {
        missingFamilies.push(family.familyId)
      }
    }
    return {
      moduleId,
      totalFamilies: allFamilies.length,
      eligibleFamilies: eligibleFamilies.length,
      familiesWithQuestions,
      missingFamilies,
      tierCoverage,
    }
  })
  return {
    group,
    allowedTiers: allowedArray,
    modules,
  }
}
